import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, FolderOpen, Puzzle, LogIn, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const STEPS = [
  { icon: FolderOpen, title: "Unzip the package", body: <>Download the zip below, right-click it → <b>Extract All…</b> and keep the extracted folder somewhere permanent (e.g. Documents). Chrome loads the extension from this folder, so don't delete it. The folder you pick in the next step must directly contain <code className="font-mono text-xs text-zinc-200">manifest.json</code> — if you see another folder inside, open it first.</> },
  { icon: Puzzle, title: "Load it in Chrome", body: <>Open <code className="font-mono text-xs text-zinc-200">chrome://extensions</code>, switch on <b>Developer mode</b> (top right), click <b>Load unpacked</b> and pick the folder containing <code className="font-mono text-xs text-zinc-200">manifest.json</code>. Pin “Passcode Hub” from the puzzle-piece menu so the icon stays visible. Updating later: replace the folder's files with the new download and click the reload ↻ arrow on the extension card.</> },
  { icon: LogIn, title: "Sign in once", body: "Click the toolbar icon, enter your team email and the 6-digit code we email you. You stay signed in on that browser for 30 days. The hub address is pre-filled." },
  { icon: FlaskConical, title: "Try it", body: <>Open the <a className="text-emerald-400 underline" href="/test-login" target="_blank" rel="noreferrer">test page</a>, press “Send test code”, and watch the Fill prompt appear beside the code field.</> },
];

export default function Install() {
  const [busy, setBusy] = useState(false);
  const config = useQuery({ queryKey: ["config"], queryFn: () => api.get("/config").then((r) => r.data) });

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/extension/download", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "passcode-hub-extension.zip" });
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rise flex max-w-3xl flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-100">Install the extension</h1>
        <p className="mt-1 text-sm text-zinc-400">Chrome on desktop (Edge and Brave will likely work too). Phase 1 installs via developer mode; a Web Store listing comes later so it auto-updates.</p>
      </div>

      <div className="surface flex flex-wrap items-center justify-between gap-4 p-5">
        <div>
          <p className="eyebrow mb-1">Package</p>
          <p className="font-display text-lg font-semibold text-zinc-100">passcode-hub-extension.zip</p>
          <p className="mt-1 font-mono text-xs text-zinc-500">Hub address baked in: {config.data?.public_base_url}</p>
        </div>
        <Button onClick={download} disabled={busy} className="h-11 gap-2 px-5 font-semibold" data-testid="download-extension-button">
          <Download className="h-4 w-4" /> {busy ? "Preparing…" : "Download extension"}
        </Button>
      </div>

      <ol className="flex flex-col gap-3">
        {STEPS.map(({ icon: Icon, title, body }, i) => (
          <li key={title} className="surface flex gap-4 p-5" data-testid={`install-step-${i + 1}`}>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#181C24] text-emerald-400"><Icon className="h-4 w-4" /></span>
            <div>
              <p className="font-display text-base font-semibold text-zinc-100"><span className="mr-2 font-mono text-xs text-zinc-500">0{i + 1}</span>{title}</p>
              <p className="mt-1 text-sm leading-relaxed text-zinc-400">{body}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="surface p-5 text-sm text-zinc-400">
        <p className="eyebrow mb-2">Good to know</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Chrome shows a one-time “Disable developer mode extensions” nudge on start while installed this way. Dismiss it; publishing to the Web Store (Phase 2) removes it.</li>
          <li>Nothing is filled without clicking <b>Fill</b>. The extension keeps only a sign-in token; codes are fetched live and never stored in the browser.</li>
          <li>Turn the prompt off for a specific site from the popup if it gets in the way.</li>
          <li>Send teammates this page — each person signs in with their own email; the admin must add them on the Members page first.</li>
        </ul>
      </div>
    </div>
  );
}
